import { FC, PropsWithChildren, useCallback, useEffect, useMemo } from 'react'

import useRouter from 'hooks/useRouter'
import { Status } from 'components/Status'
import { Container } from 'components/Container/Container'
import { Menu } from 'components/Menu/Menu'
import { useContractDetailsContext } from 'features/ContractDetails/Providers/ContractDetailsContextProvider'
import ContractDetailsActions from 'features/ContractDetails/ContractDetailsActions/ContractDetailsActions'
import {
  CONTRACT_DETAILS_BREADCRUMBS,
  CONTRACT_STATUS,
  CONTRACT_STATUS_CLASSNAME,
  CONTRACT_STATUS_NAME
} from 'features/ContractDetails/constants'
import {
  ACTIONS,
  contractSubject,
  SUBJECTS
} from 'features/ContractDetails/Abilities'
import {
  Can,
  useContractDetailsAbility
} from 'features/ContractDetails/Providers/ContractDetailsAbilityProvider'
import { useContractDetailsPopup } from 'features/ContractDetails/Providers/ContractDetailsPopupProvider'
import { safeString } from '../../../helper/common'
import { useParams } from 'react-router-dom'
import ContractBanners from '../Banners'
import ChangeTerminationDate from '../Banners/ChangeTerminationDate'
import { Typography, TYPOGRAPHY_WEIGHT } from '../../../components/Typography'
import { daysBetweenCalculation, longDateFormat } from '../../../helper/dates'
import { ReactComponent as EventSchedule16 } from 'assets/svg/EventSchedule16.svg'
import { Tooltip } from '../../../components/Tooltip/Tooltip'
import cn from 'classnames'

const ContractDetailsContainer: FC<PropsWithChildren> = ({ children }) => {
  const router = useRouter()
  const ability = useContractDetailsAbility()
  const { tab } = useParams()

  const contractDetailsContext = useContractDetailsContext()
  const { contractStepsTimePopup } = useContractDetailsPopup()

  const {
    details,
    expiredAt,
    activeTab,
    tabs,
    isAutoGeneratedContract,
    isCreation
  } = contractDetailsContext.state
  const { setActiveTab } = contractDetailsContext.actions

  const title = details.uuid
    ? details.contract_category_name
    : 'Contract creation'
  const canOpenTimer = ability.can(
    ACTIONS.SETUP,
    contractSubject(SUBJECTS.TIMELINES, details)
  )
  const handleClickTimer = useCallback(() => {
    if (canOpenTimer) {
      contractStepsTimePopup.actions.open()
    }
  }, [canOpenTimer, contractStepsTimePopup.actions])

  useEffect(() => {
    setActiveTab(safeString(tab || 'general'))
  }, [setActiveTab, tab])

  const RenderedStatus = useMemo(
    () =>
      !isCreation && (
        <Status.Default className={CONTRACT_STATUS_CLASSNAME[details.status]}>
          {CONTRACT_STATUS_NAME[details.status]}
        </Status.Default>
      ),
    [details.status, isCreation]
  )

  const RenderedActions = useMemo(() => <ContractDetailsActions />, [])

  const RenderedMenu = useMemo(() => {
    const handleSelectTab = (e) => {
      setActiveTab(e.key)
      router.push(e.key)
    }

    return (
      <>
        <Can
          I={ACTIONS.UPLOAD}
          a={contractSubject(SUBJECTS.ITEM_LIST, details)}
        >
          <ContractBanners />
        </Can>
        <Menu
          mode="horizontal"
          onSelect={handleSelectTab}
          selectedKeys={[activeTab]}
          defaultSelectedKeys={[activeTab]}
          items={tabs}
        />
      </>
    )
  }, [activeTab, details, router, setActiveTab, tabs])

  const isContractsEndHide = useMemo(() => {
    return (
      details.status === CONTRACT_STATUS.TERMINATED ||
      details.status === CONTRACT_STATUS.DECLINED ||
      !details?.finish_date ||
      (Number(daysBetweenCalculation(details?.finish_date)) <= 0 &&
        details.status !== CONTRACT_STATUS.EXPIRED) ||
      (details.status === CONTRACT_STATUS.ACTIVE &&
        Number(
          daysBetweenCalculation(
            details.termination_date
              ? details.termination_date
              : details.finish_date
          ) <= 0
        ))
    )
  }, [details])

  const renderContractEnd = useMemo(() => {
    const amountOfDays = Number(
      daysBetweenCalculation(
        details.status === CONTRACT_STATUS.ACTIVE && details.termination_date
          ? details.termination_date
          : details.finish_date
      )
    )
    return (
      <div
        className={cn('row align-center gap-4 contracts-end-wrapper', {
          withTooltip: details.status !== CONTRACT_STATUS.EXPIRED,
          withBorder: !!expiredAt
        })}
      >
        <EventSchedule16 />
        {details.status !== CONTRACT_STATUS.EXPIRED ? (
          <Tooltip
            placement={'bottomLeft'}
            title={`Ends on ${
              longDateFormat(
                details.status === CONTRACT_STATUS.ACTIVE &&
                  details.termination_date
                  ? details.termination_date
                  : details.finish_date
              ) || '-'
            }`}
          >
            <div>
              <Typography.Label weight={TYPOGRAPHY_WEIGHT.SEMI_BOLD}>
                Contract ends in {amountOfDays || '-'} day
                {amountOfDays > 1 ? 's' : ''}
              </Typography.Label>
            </div>
          </Tooltip>
        ) : (
          <Typography.Label weight={TYPOGRAPHY_WEIGHT.SEMI_BOLD}>
            <>Expired on {longDateFormat(details.finish_date) || '-'}</>
          </Typography.Label>
        )}
      </div>
    )
  }, [details, expiredAt])

  return (
    <Can I={ACTIONS.VIEW} a={contractSubject(SUBJECTS.CONTRACT, details)}>
      <Container
        title={title}
        subtitle={details.vendor?.name}
        expiresAt={expiredAt}
        contractsEnds={!isContractsEndHide && renderContractEnd}
        onClickTimer={isAutoGeneratedContract ? handleClickTimer : undefined}
        breadcrumbs={CONTRACT_DETAILS_BREADCRUMBS(title, router.query)}
        status={RenderedStatus}
        actions={RenderedActions}
        menu={RenderedMenu}
        reasonComponent={<ChangeTerminationDate />}
        previentTimerInteraction={!canOpenTimer}
      >
        {children}
      </Container>
    </Can>
  )
}

export default ContractDetailsContainer
